/**
 * DemoResetButton — floating bottom-right "Reset Demo" button for the Acme
 * Law Firm sandbox. Mount only when VITE_DEMO_MODE=true. Two-step confirm
 * to avoid accidental wipes.
 *
 * Reuses the unlock token issued by DemoPasswordGate (stored in
 * localStorage); backend invokes scripts/seed_demo_data.py with --reset-demo
 * to refresh all [DEMO] rows. No client-side passphrase — rotating
 * DEMO_PASSPHRASE on the server invalidates the token after restart and the
 * user simply re-unlocks at the gate.
 */
import { useState } from 'react';
import api from '../api/client';

const TOKEN_STORAGE_KEY = 'demo_unlock_token';

type State = 'idle' | 'confirm' | 'resetting' | 'done';

export default function DemoResetButton() {
  const [state, setState] = useState<State>('idle');

  const handleReset = async () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      alert('Demo session expired — please refresh and re-enter the passphrase.');
      return;
    }
    setState('resetting');
    try {
      await api.post('/demo/reset', null, { headers: { 'x-demo-token': token } });
      setState('done');
      setTimeout(() => window.location.reload(), 1200);
    } catch {
      setState('idle');
      // Most likely a stale token after DEMO_PASSPHRASE rotation + restart.
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      alert('Reset failed — your demo session may have expired. Refresh the page and re-enter the passphrase.');
    }
  };

  if (state === 'resetting') {
    return <div style={floatingStyle('#6366f1')}>↺ Resetting…</div>;
  }
  if (state === 'done') {
    return <div style={floatingStyle('#10b981')}>✓ Reset complete</div>;
  }
  if (state === 'confirm') {
    return (
      <div style={{ ...floatingStyle('#f43f5e'), gap: 8, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem' }}>Reset all demo data?</span>
        <button onClick={handleReset} style={btnStyle('#dc2626')}>Yes</button>
        <button onClick={() => setState('idle')} style={btnStyle('#374151')}>No</button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setState('confirm')}
      title="Reset demo data to defaults"
      style={{ ...floatingStyle('#374151'), cursor: 'pointer', border: 'none' }}
    >
      ↺ Reset Demo
    </button>
  );
}

function floatingStyle(bg: string): React.CSSProperties {
  return {
    position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
    background: bg, color: '#fff', fontSize: '0.78rem', fontWeight: 600,
    padding: '8px 14px', borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    display: 'flex', alignItems: 'center',
  };
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    background: bg, color: '#fff', border: 'none', borderRadius: 4,
    padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
  };
}
